/**
 * The projects this device has worked on, summarised for the client and site records (client,
 * address, last inspection, next due date): kept in localStorage, so the client list also shows
 * projects that are only files on this computer. Cloud projects come from the server as well
 * (GET /api/teams/:id/sites) and the newest summary of a project wins.
 */
import { siteSummary, type Project, type SiteSummary } from '@elec/domain';

const KEY = 'tablo.sites.v1';
const MAX = 500;

export interface IndexedSite extends SiteSummary {
  /** Where the project lives: a cloud project (its workspace) or a file on this device. */
  where: 'device' | 'cloud';
  teamId?: string;
  /** The file name it was last saved as, for "open it with File → Open". */
  file?: string;
}

export function readSiteIndex(): IndexedSite[] {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as Record<string, IndexedSite>) : {};
    return Object.values(v).filter((s) => s && typeof s.projectId === 'string' && s.client);
  } catch {
    return [];
  }
}

function write(list: IndexedSite[]) {
  try {
    const newest = [...list].sort((a, b) => b.seen.localeCompare(a.seen)).slice(0, MAX);
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(newest.map((s) => [s.projectId, s]))));
  } catch {
    // Private mode or storage full: the list then only shows cloud projects.
  }
}

/** Is there anything to file the project under (a client or an address)? */
export const hasClient = (p: Project) =>
  !!(p.meta.ownerName.trim() || p.meta.ownerAddress.trim() || p.meta.installationAddress.trim());

/** Record (or refresh) the open project's summary. Projects with no client or address are skipped. */
export function rememberSite(p: Project, where: Omit<IndexedSite, keyof SiteSummary>): void {
  if (!hasClient(p)) return;
  const list = readSiteIndex().filter((s) => s.projectId !== p.id);
  const s: IndexedSite = { ...siteSummary(p), ...where };
  write([...list, s]);
}

export function forgetSite(projectId: string): void {
  write(readSiteIndex().filter((s) => s.projectId !== projectId));
}

/** This device's summaries and the server's, one per project (the newest). */
export function mergeSites(device: IndexedSite[], cloud: IndexedSite[]): IndexedSite[] {
  const by = new Map<string, IndexedSite>();
  for (const s of [...device, ...cloud]) {
    const cur = by.get(s.projectId);
    // A project in the cloud stays a cloud project, whichever summary is newer.
    if (!cur || s.seen > cur.seen)
      by.set(s.projectId, cur?.where === 'cloud' ? { ...s, where: 'cloud', teamId: cur.teamId } : s);
    else if (s.where === 'cloud')
      by.set(s.projectId, { ...cur, where: 'cloud', ...(s.teamId ? { teamId: s.teamId } : {}) });
  }
  return [...by.values()];
}
